import React from "react";
import cx from 'classnames';

import './context-menu.styles.scss';

export type ContextMenuProps = {
  x: number;
  y: number;
  onClose: () => void;
  onCut: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onFillDown: () => void;
  onFillUp: () => void;
  onOpenDatasheet: () => void;
  onOpenPedigree: () => void;
  canCut: boolean;
  canCopy: boolean;
  canPaste: boolean;
  canFill: boolean;
  canOpenDatasheet: boolean;
  canOpenPedigree: boolean;
  onAddRow: (() => void) | undefined;
  canAddRow: boolean;
  canDeleteRow: boolean;
  onDeleteRow: (() => void) | undefined;
};

export const ContextMenu: React.FC<ContextMenuProps> = (props) => {
  const { 
    x, 
    y, 
    onClose, 
    onCut, 
    onCopy, 
    onPaste, 
    onFillDown, 
    onFillUp,
    onOpenDatasheet,
    onOpenPedigree,
    canCut,
    canCopy,
    canPaste,
    canFill,
    canOpenDatasheet,
    canOpenPedigree,
    onAddRow,
    canAddRow,
    canDeleteRow,
    onDeleteRow
  } = props;

  const [showFillSubmenu, setShowFillSubmenu] = React.useState(false);
  const [adjustedPosition, setAdjustedPosition] = React.useState({ x, y });
  const contextMenuRef = React.useRef<HTMLDivElement>(null);

  const handleClick = (action: () => void) => {
    action();
    onClose();
  };

  const handleFillClick = () => {
    setShowFillSubmenu(!showFillSubmenu);
  };

  // Adjust position to stay within viewport
  React.useLayoutEffect(() => {
    if (!contextMenuRef.current) return;

    const menu = contextMenuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8; // Minimum distance from viewport edge

    let adjustedX = x;
    let adjustedY = y;

    // Check horizontal boundaries
    if (x + rect.width + padding > viewportWidth) {
      adjustedX = Math.max(padding, viewportWidth - rect.width - padding);
    }
    if (adjustedX < padding) {
      adjustedX = padding;
    }

    // Check vertical boundaries
    if (y + rect.height + padding > viewportHeight) {
      adjustedY = Math.max(padding, viewportHeight - rect.height - padding);
    }
    if (adjustedY < padding) {
      adjustedY = padding;
    }

    setAdjustedPosition({ x: adjustedX, y: adjustedY });
  }, [x, y]);

  // Use useEffect to handle clicks outside the context menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleContextMenu = (event: MouseEvent) => {
      // If right-click happens outside the context menu, close it and allow the event to propagate
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        onClose();
        // Don't prevent default - let the right-click reach the cell underneath
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('contextmenu', handleContextMenu);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [onClose]);

  return (
    <>
      <div 
        ref={contextMenuRef}
        className="context-menu"
        style={{ 
          left: adjustedPosition.x, 
          top: adjustedPosition.y 
        }}
      >
        <div 
          className={cx('context-menu-item', { disabled: !canCut })}
          onClick={() => canCut && handleClick(onCut)}
        >
          Cut
        </div>
        <div 
          className={cx('context-menu-item', { disabled: !canCopy })}
          onClick={() => canCopy && handleClick(onCopy)}
        >
          Copy
        </div>
        <div 
          className={cx('context-menu-item', { disabled: !canPaste })}
          onClick={() => canPaste && handleClick(onPaste)}
        >
          Paste
        </div>
        <div className="context-menu-separator" />
        <div className="context-menu-submenu-container">
          <div 
            className={cx('context-menu-item', 'context-menu-item-with-submenu', { disabled: !canFill })}
            onClick={() => canFill && handleFillClick()}
            onMouseEnter={() => canFill && setShowFillSubmenu(true)}
          >
            Fill
            <span className="context-menu-arrow">▶</span>
          </div>
          {showFillSubmenu && (
            <div className="context-menu-submenu">
              <div 
                className="context-menu-item"
                onClick={() => handleClick(onFillDown)}
              >
                Fill Down
              </div>
              <div 
                className="context-menu-item"
                onClick={() => handleClick(onFillUp)}
              >
                Fill Up
              </div>
            </div>
          )}
        </div>
        <div className="context-menu-separator" />
        <div 
          className={cx('context-menu-item', { disabled: !canOpenDatasheet })}
          onClick={() => canOpenDatasheet && handleClick(onOpenDatasheet)}
        >
          Open Datasheet
        </div>
        <div 
          className={cx('context-menu-item', { disabled: !canOpenPedigree })}
          onClick={() => canOpenPedigree && handleClick(onOpenPedigree)}
        >
          Open Pedigree
        </div>
        {(canAddRow || canDeleteRow) && onAddRow && onDeleteRow && <>
        <div className="context-menu-separator" />
        <div 
            className={cx('context-menu-item', { disabled: !canAddRow })}
            onClick={() => canAddRow && handleClick(onAddRow)}
          >
            Add Row
        </div>
        <div 
            className={cx('context-menu-item', { disabled: !canDeleteRow })}
            onClick={() => canDeleteRow && handleClick(onDeleteRow)}
          >
            Delete Row
        </div></>}
      </div>
      
    </>
  );
};
